import { apiProblemText } from "../../../api/api-error.js";
import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `Organization` → Create an organization — `/start/organization` (plan-014 §4, design §27.15.1). Owned by
 * plan-014 unit **B1** (FR12-ONB-01, FR12-ONB-03, FR12-ONB-05).
 *
 * THREE RESUMABLE STEPS (FR12-ONB-01):
 *
 *   1. identity      who is signed in, and whether that email is verified — an organization is created by a
 *                    verified person (the API refuses `email_unverified` otherwise);
 *   2. organization  the prototype's form: Organization name, Country, Billing timezone (shown, never chosen:
 *                    the platform's for every new organization), First workspace — the terms to accept, and
 *                    the trial notice ("30 free minutes" only when this verified person has not had one);
 *   3. voice_agent   the organization exists, the person owns it and its workspace; set up the first agent.
 *
 * Legal billing details are not asked: they are collected when a purchase or a financial document needs them.
 *
 * THE DRAFT LIVES ON THE SERVER (`GET/PUT /v1/me/onboarding-draft`, FR12-ONB-05): the step the page opens on
 * is the first one the draft has not completed, the inputs are saved as the person leaves a field, and
 * nothing is kept in browser storage. The organization step is completed only by creating the organization.
 *
 * THE IDEMPOTENCY KEY of the creation is held here beside the form contents it describes: the same key for a
 * retry of the same form (a lost answer cannot create a second organization), a new one when anything changed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button, Field, Icon, Input, Select, Tag } from "@wetalk/ui";
import { isSupportedCountry } from "@wetalk/domain";
import { useRef, useState, type FormEvent, type ReactNode } from "react";

import type { AuthSessionView } from "../../../api/gateway.js";
import type { OnboardingDraftView, SaveOnboardingDraftRequestView } from "../../../api/slice/account.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY } from "../../../api/query.js";
import { GLYPH } from "../../../shell/glyph.js";
import { ResendVerificationButton } from "../../mail-landing/resend-verification.js";

import {
  FIRST_WORKSPACE_NAME_MAXIMUM,
  ORGANIZATION_NAME_LENGTH,
  attemptKeyFor,
  countryOptions,
  currentStepOf,
  organizationFormOf,
  organizationProblem,
  stepNumberOf,
  trialLengthOf,
  trialNoticeOf,
  type Attempt,
  type OrganizationForm,
} from "./account-creation-step.js";

import "./account-creation.css";
import { PublicFrame, PublicKicker } from "../../auth/public-frame.js";

/** The page's search parameters. The step comes from the server-side draft, never the address bar. */
export interface AccountCreationSearch {}

/**
 * The route's `validateSearch`: a total function over the untrusted address bar, so a hand-edited URL
 * still opens a valid page.
 */
export function accountCreationSearchOf(_raw: Readonly<Record<string, unknown>>): AccountCreationSearch {
  return {};
}

export interface AccountCreationFlowProps {
  readonly search: AccountCreationSearch;
  readonly onSearch: (next: AccountCreationSearch) => void;
}

/** The draft's own query key — under the console's keys, cleared with them on a new sign-in. */
const ONBOARDING_DRAFT_KEY = ["onboarding-draft"] as const;

const LEDE =
  portalText("core", "start.for_a_business_that_will_pay_for_its_own_talk_time_if_someone_has_278d47");
export function ledeText(): typeof LEDE { return portalText("core", "start.for_a_business_that_will_pay_for_its_own_talk_time_if_someone_has_278d47"); }


export function AccountCreationFlow(_props: AccountCreationFlowProps): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const draft = useQuery({ queryKey: ONBOARDING_DRAFT_KEY, queryFn: () => gateway.onboardingDraft() });

  return (
    // ADR 0056 (portal amendment): creating an organization is a way in (a page outside the app shell, owner answer 61), so it is drawn
    // in the public design's frame like Log in and Sign up.
    <PublicFrame width="wide">
      <div className="c-onboarding">
        {draft.isPending ? (
          <p className="c-onboarding-status" role="status">{portalText("core", "start.loading_your_progress_be6150")}</p>
        ) : draft.isError ? (
          <p className="wt-field-error" role="alert">
            {apiProblemText(draft.error, "")}
          </p>
        ) : (
          <Steps draft={draft.data} />
        )}
      </div>
    </PublicFrame>
  );
}

function Header({ step }: { readonly step: number }): ReactNode {
  usePortalTranslation("core");
  return (
    <>
      <PublicKicker>{portalText("core", "start.step_of_3_your_progress_is_saved_f85613", { value0: String(step) })}</PublicKicker>
      <h1 className="c-onboarding-title">{portalText("core", "start.create_an_organization_b73106")}</h1>
      <p className="c-onboarding-lede">{ledeText()}</p>
    </>
  );
}

function Steps({ draft }: { readonly draft: OnboardingDraftView }): ReactNode {
  usePortalTranslation("core");
  const [created, setCreated] = useState<{ readonly trialGranted: boolean } | null>(null);
  const step = currentStepOf(draft);
  return (
    <>
      <Header step={stepNumberOf(step)} />
      {step === "identity" ? <IdentityStep draft={draft} /> : null}
      {step === "organization" ? <OrganizationStep draft={draft} onCreated={setCreated} /> : null}
      {step === "voice_agent" ? <VoiceAgentStep draft={draft} created={created} /> : null}
    </>
  );
}

function Notice({
  tone,
  glyph,
  title,
  detail,
}: {
  readonly tone: "accent" | "neutral";
  readonly glyph: (typeof GLYPH)[keyof typeof GLYPH];
  readonly title: string;
  readonly detail: string;
}): ReactNode {
  usePortalTranslation("core");
  return (
    <div className={`c-onboarding-notice c-onboarding-notice-${tone}`}>
      <Icon glyph={glyph} size="lg" />
      <span className="c-onboarding-notice-text">
        <span className="c-onboarding-notice-title">{title}</span>
        <span className="c-onboarding-notice-detail">{detail}</span>
      </span>
    </div>
  );
}

/** Saves the draft and refreshes the page's copy of it. */
function useSaveDraft() {
  const gateway = useGateway();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveOnboardingDraftRequestView) => gateway.saveOnboardingDraft(request),
    onSuccess: (saved) => {
      queryClient.setQueryData(ONBOARDING_DRAFT_KEY, saved);
    },
  });
}

// ---------------------------------------------------------------------------------------
// Step 1 — identity
// ---------------------------------------------------------------------------------------

function IdentityStep({ draft }: { readonly draft: OnboardingDraftView }): ReactNode {
  usePortalTranslation("core");
  const save = useSaveDraft();
  return (
    <section className="c-onboarding-block" aria-labelledby="onboarding-identity">
      <div className="c-onboarding-head">
        <div>
          <p id="onboarding-identity" className="c-onboarding-block-title">{portalText("core", "start.your_identity_ced590")}</p>
          <p className="c-onboarding-block-dek">{portalText("core", "start.you_will_be_the_owner_of_the_organization_and_of_its_first_worksp_71daa5")}</p>
        </div>
        <Button
          variant="primary"
          disabled={!draft.emailVerified || save.isPending}
          onClick={() => {
            save.mutate({ stepCompleted: ["identity"], input: draft.input });
          }}
        >{portalText("core", "auth.continue_2e0262")}</Button>
      </div>
      <div className="c-onboarding-row">
        <Icon glyph={draft.emailVerified ? GLYPH.shieldCheck : GLYPH.warning} size="lg" />
        <span className="c-onboarding-row-text">
          <span className="c-onboarding-row-title">{draft.emailAddress}</span>
          <span className="c-onboarding-row-dek">{portalText("core", "start.the_address_you_are_signed_in_with_3af576")}</span>
        </span>
        <Tag tone={draft.emailVerified ? "accent" : "neutral"}>{draft.emailVerified ? portalText("core", "start.verified_aed3b8") : portalText("core", "start.not_verified_fbc0fa")}</Tag>
      </div>
      {draft.emailVerified ? null : (
        <Notice
          tone="neutral"
          glyph={GLYPH.warning}
          title={portalText("core", "start.verify_your_email_address_first_15e242")}
          detail={portalText("core", "start.an_organization_is_created_by_a_verified_person_and_a_trial_is_gi_fd9679")}
        />
      )}
      {/* plan-014 review I-17: an expired or lost link is not a dead end. */}
      {draft.emailVerified ? null : <ResendVerificationButton refresh={[ONBOARDING_DRAFT_KEY, QUERY_KEY.authSession]} />}
      {save.isError ? (
        <p className="wt-field-error" role="alert">
          {apiProblemText(save.error, "")}
        </p>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------------------
// Step 2 — the organization
// ---------------------------------------------------------------------------------------

function OrganizationStep({
  draft,
  onCreated,
}: {
  readonly draft: OnboardingDraftView;
  readonly onCreated: (created: { readonly trialGranted: boolean }) => void;
}): ReactNode {
  usePortalTranslation("core");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const save = useSaveDraft();
  const [form, setForm] = useState<OrganizationForm>(() => organizationFormOf(draft));
  const [problem, setProblem] = usePortalMessage();
  const attempt = useRef<Attempt | null>(null);
  const notice = trialNoticeOf(draft.trialEligibility, draft.trialGrantSecond);

  const create = useMutation({
    mutationFn: (request: Parameters<typeof gateway.createOrganization>[0]) => gateway.createOrganization(request),
    onSuccess: async (made) => {
      // The session is now scoped to the new organization: every cached read of the previous scope goes
      // (as `useAdoptAuthSession` does on a sign-in), except this page's own draft, which is re-read.
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== ONBOARDING_DRAFT_KEY[0] && query.queryKey[0] !== QUERY_KEY.authSession[0] });
      queryClient.setQueryData<AuthSessionView | null>(QUERY_KEY.authSession, made.authSession);
      onCreated({ trialGranted: made.trialGranted });
      queryClient.setQueryData(ONBOARDING_DRAFT_KEY, await gateway.onboardingDraft());
    },
    onError: (raised: Error) => {
      setProblem(() => (apiProblemText(raised, "")));
    },
  });

  const saveInputs = (next: OrganizationForm): void => {
    save.mutate({
      stepCompleted: draft.stepCompleted.filter((one) => one !== "organization"),
      input: {
        accountName: next.accountName.trim() === "" ? null : next.accountName.trim(),
        country: next.country,
        firstWorkspaceName: next.firstWorkspaceName.trim() === "" ? null : next.firstWorkspaceName.trim(),
      },
    });
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setProblem(null);
    const refused = organizationProblem(form);
    if (refused !== null || form.country === null) {
      setProblem(() => (organizationProblem(form) ?? portalText("core", "start.choose_the_country_the_organization_is_in_95a910")));
      return;
    }
    const request = {
      accountName: form.accountName.trim(),
      country: form.country,
      firstWorkspaceName: form.firstWorkspaceName.trim(),
      termsRevision: draft.terms.revision,
    };
    attempt.current = attemptKeyFor(attempt.current, JSON.stringify(request), () => globalThis.crypto.randomUUID());
    create.mutate({ ...request, idempotencyKey: attempt.current.idempotencyKey });
  };

  return (
    <form className="c-onboarding-block" onSubmit={onSubmit} aria-labelledby="onboarding-organization">
      <div className="c-onboarding-head">
        <div>
          <p id="onboarding-organization" className="c-onboarding-block-title">{portalText("core", "start.the_organization_ab4ee0")}</p>
          <p className="c-onboarding-block-dek">{portalText("core", "start.legal_and_tax_details_are_asked_for_later_when_you_first_buy_some_bc15a8")}</p>
        </div>
        <Button variant="primary" type="submit" disabled={create.isPending}>
          {create.isPending ? portalText("core", "start.creating_94d7d8") : portalText("core", "auth.continue_2e0262")}
        </Button>
      </div>
      <div
        className="c-onboarding-form"
        onBlur={() => {
          saveInputs(form);
        }}
      >
        <Field label={portalText("core", "start.organization_name_9eab9e")}>
          {(control) => (
            <Input
              {...control}
              name="accountName"
              value={form.accountName}
              placeholder={portalText("core", "start.e_g_trattoria_da_luca_f7a380")}
              autoComplete="organization"
              maxLength={ORGANIZATION_NAME_LENGTH.maximum}
              onChange={(event) => {
                setForm({ ...form, accountName: event.currentTarget.value });
              }}
            />
          )}
        </Field>
        <Field label={portalText("core", "auth.country_d523eb")} description={portalText("core", "start.decides_tax_rules_on_your_invoices_4ab97b")}>
          {(control) => (
            <Select
              {...control}
              name="country"
              value={form.country ?? ""}
              onChange={(event) => {
                const chosen = event.currentTarget.value;
                setForm({ ...form, country: isSupportedCountry(chosen) ? chosen : null });
              }}
            >
              <option value="" disabled>{portalText("core", "auth.choose_a_country_7843c8")}</option>
              {countryOptions().map((one) => (
                <option key={one.code} value={one.code}>
                  {one.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label={portalText("core", "start.billing_timezone_02603c")}
          description={portalText("core", "start.billing_months_start_and_end_in_this_time_zone_an_owner_can_chang_80f70f")}
        >
          {(control) => <Input {...control} name="billingTimeZone" value={draft.billingTimeZone.replaceAll("_", " ")} readOnly />}
        </Field>
        <Field label={portalText("core", "start.first_workspace_90d647")} description={portalText("core", "start.you_can_add_more_later_b16684")}>
          {(control) => (
            <Input
              {...control}
              name="firstWorkspaceName"
              value={form.firstWorkspaceName}
              maxLength={FIRST_WORKSPACE_NAME_MAXIMUM}
              onChange={(event) => {
                setForm({ ...form, firstWorkspaceName: event.currentTarget.value });
              }}
            />
          )}
        </Field>
      </div>
      <label className="c-onboarding-terms">
        <input
          type="checkbox"
          name="termsAccepted"
          checked={form.termsAccepted}
          onChange={(event) => {
            setForm({ ...form, termsAccepted: event.currentTarget.checked });
          }}
        />
        <span>{portalText("core", "start.i_accept_the_74b561")}{" "}
          <a href={draft.terms.termsUrl} target="_blank" rel="noreferrer">{portalText("core", "start.terms_ce620d")}</a>{" "}{portalText("core", "start.and_the_38d402")}{" "}
          <a href={draft.terms.privacyUrl} target="_blank" rel="noreferrer">{portalText("core", "start.privacy_policy_478dbb")}</a>{" "}
          {portalText("core", "start._revision_536f9f", { value0: draft.terms.revision })}
        </span>
      </label>
      {problem === null ? null : (
        <p className="wt-field-error" role="alert">
          {problem}
        </p>
      )}
      <Notice tone={notice.tone} glyph={GLYPH.gift} title={notice.title} detail={notice.detail} />
    </form>
  );
}

// ---------------------------------------------------------------------------------------
// Step 3 — the first agent
// ---------------------------------------------------------------------------------------

function VoiceAgentStep({
  draft,
  created,
}: {
  readonly draft: OnboardingDraftView;
  readonly created: { readonly trialGranted: boolean } | null;
}): ReactNode {
  usePortalTranslation("core");
  const navigate = useNavigate();
  const save = useSaveDraft();
  const name = draft.input.accountName ?? portalText("core", "start.your_organization_3e5163");
  return (
    <section className="c-onboarding-block" aria-labelledby="onboarding-voice-agent">
      <Notice
        tone="accent"
        glyph={GLYPH.checkCircle}
        title={portalText("core", "start._is_ready_e45556", { value0: name })}
        detail={
          created === null
            ? portalText("core", "start.the_organization_and_its_first_workspace_exist_you_are_the_owner_89d3b0")
            : created.trialGranted
              ? portalText("core", "start.organization_and_default_workspace_created_you_are_the_owner_of_b_b19636", { value0: trialLengthOf(draft.trialGrantSecond) })
              : portalText("core", "start.organization_and_default_workspace_created_you_are_the_owner_of_b_d7b3d8")
        }
      />
      <div className="c-onboarding-head">
        <div>
          <p id="onboarding-voice-agent" className="c-onboarding-block-title">{portalText("core", "start.set_up_your_first_agent_02e1c3")}</p>
          <p className="c-onboarding-block-dek">{portalText("core", "start.pick_a_ready_made_template_add_your_own_content_and_take_your_fir_d6662f")}</p>
        </div>
        <Button
          variant="primary"
          disabled={save.isPending}
          onClick={() => {
            save.mutate(
              { stepCompleted: ["identity", "voice_agent"], input: draft.input },
              {
                onSuccess: () => {
                  void navigate({ to: "/agents/new" });
                },
              },
            );
          }}
        >{portalText("core", "start.set_up_an_agent_f60a26")}</Button>
      </div>
      <p className="c-onboarding-later">
        <Link to="/dashboard">{portalText("core", "start.i_will_do_this_later_4cb988")}</Link>
      </p>
      {save.isError ? (
        <p className="wt-field-error" role="alert">
          {apiProblemText(save.error, "")}
        </p>
      ) : null}
    </section>
  );
}
